import { ClockImpactType, IGameEvent, IPlayerEvent } from 'src/app/states/event/event.models';
import { IGamePeriod, TPeriodScore } from 'src/app/states/game/game.models';

/** Lower-cased player-event-definition name that identifies a goal, used for live score counting. */
export const GOAL_PLAYER_EVENT_DEFINITION_NAME = 'goal';

/**
 * Formats a duration in seconds as a period clock string `M:SS.cc`, where `cc`
 * is hundredths of a second.
 *
 * @param seconds - The duration to format, in seconds.
 * @returns The formatted clock string in the form `M:SS.cc`.
 */
export const secondsToPeriodTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${minutes}:${secs.toFixed(2).padStart(5, '0')}`;
};

/**
 * Computes the official elapsed period time (in seconds) at a given video frame, anchored to the
 * most recent clock-impacting game event.
 *
 * @param currentFrame The current video frame (videoSeconds * frameRate)
 * @param frameRate The frame rate of the period's video
 * @param periodNumber The selected period
 * @param gameEvents All game events for the game
 * @param clockImpactByDefId Map of game-event definition id to its clock impact
 * @returns The elapsed period time in seconds at the current frame
 */
export const computeElapsedPeriodTime = (
    currentFrame: number,
    frameRate: number,
    periodNumber: number,
    gameEvents: IGameEvent[],
    clockImpactByDefId: Record<string, ClockImpactType>
// eslint-disable-next-line complexity
): number => {
    let referenceEvent: IGameEvent | undefined;

    for (const event of gameEvents) {
        const clockImpact = clockImpactByDefId[event.defId];
        const isClockEvent = clockImpact !== undefined && clockImpact !== ClockImpactType.none;

        if (Number(event.period) !== Number(periodNumber) || event.frame > currentFrame || !isClockEvent) {
            continue;
        }

        if (!referenceEvent || event.frame > referenceEvent.frame) {
            referenceEvent = event;
        }
    }

    if (!referenceEvent) {
        return 0;
    }

    const clockRunning = clockImpactByDefId[referenceEvent.defId] === ClockImpactType.start;
    const elapsedSinceReference = clockRunning && frameRate > 0
        ? (currentFrame - referenceEvent.frame) / frameRate
        : 0;

    return referenceEvent.periodTime + elapsedSinceReference;
};

/**
 * Computes the count-up clock value at the start of the given period (its "regulation offset"): the
 * cumulative regulation length of every prior period.
 *
 * @param periods Per-period metadata keyed by period number, including expected/actual lengths
 * @param periodNumber The selected period
 * @returns The cumulative regulation length (in seconds) of all periods before `periodNumber`
 */
export const computeRegulationOffset = (
    periods: Record<string, IGamePeriod> | null,
    periodNumber: number
): number => {
    let offset = 0;

    for (let priorPeriod = 1; priorPeriod < periodNumber; priorPeriod++) {
        offset += periods?.[`${priorPeriod}`]?.lengths.expected ?? 0;
    }

    return offset;
};

/**
 * Computes the count-up game clock (in seconds, e.g. soccer) at a given video frame from the loaded
 * player events.
 *
 * Game (clock) events can't drive the clock (no usable time, frames off the video timeline), so we use
 * player events, which are dense and carry an accurate `gameTime` on the video's frame basis. We anchor
 * to the nearest player event at or before `currentFrame` and add the video time elapsed since it — valid
 * for count-up clocks, which never pause (stoppage is added).
 *
 * Returns `regulationOffset` (the clock value at the period's start, e.g. 45:00 for the 2nd half) before
 * the first event or when no timed player events are loaded for the period.
 *
 * @param currentFrame The current video frame (videoSeconds * frameRate)
 * @param frameRate The frame rate of the period's video
 * @param periodNumber The selected period
 * @param playerEvents All player events for the game
 * @param regulationOffset The count-up clock value at the start of the period (`(period - 1) * regulation`)
 * @returns The count-up game clock in seconds at the current frame
 */
export const computeCountUpGameTime = (
    currentFrame: number,
    frameRate: number,
    periodNumber: number,
    playerEvents: IPlayerEvent[],
    regulationOffset: number
// eslint-disable-next-line complexity
): number => {
    let anchor: IPlayerEvent | undefined;
    let earliest: IPlayerEvent | undefined;

    for (const event of playerEvents) {
        if (Number(event.period) !== Number(periodNumber) || !Number.isFinite(event.gameTime)) {
            continue;
        }

        if (!earliest || event.frame < earliest.frame) {
            earliest = event;
        }

        if (event.frame <= currentFrame && (!anchor || event.frame > anchor.frame)) {
            anchor = event;
        }
    }

    const reference = anchor ?? earliest;
    if (!reference) {
        return regulationOffset;
    }

    const elapsedSinceReference = frameRate > 0 ? (currentFrame - reference.frame) / frameRate : 0;

    return Math.max(reference.gameTime + elapsedSinceReference, regulationOffset);
};

/**
 * Adds a team-keyed goal tally into an accumulating score, in place.
 *
 * @param score The running score to add into, keyed by team ID
 * @param teamScores The goals to add, keyed by team ID
 */
const addTeamScores = (score: Record<string, number>, teamScores: Record<string, number>): void => {
    for (const [teamId, goals] of Object.entries(teamScores)) {
        score[teamId] = (score[teamId] ?? 0) + goals;
    }
};

/**
 * Computes the running score (keyed by team ID) at the current video position.
 *
 * @param periodScore Per-period scores keyed by period then team ID (may omit periods with no goals)
 * @param selectedPeriodNumber The period currently loaded in the video player
 * @param currentFrame The current video frame (videoSeconds * frameRate)
 * @param playerEvents The loaded player events (only the current period is loaded in state)
 * @param goalDefinitionIds Set of player-event-definition ids that represent a goal
 * @returns The running score keyed by team ID
 */
export const computeLiveScore = (
    periodScore: TPeriodScore | null,
    selectedPeriodNumber: number,
    currentFrame: number,
    playerEvents: IPlayerEvent[],
    goalDefinitionIds: Set<string>
): Record<string, number> => {
    const score: Record<string, number> = {};

    // Periods before the selected one are final and contribute their full score.
    Object.entries(periodScore ?? {})
        .filter(([periodKey]) => parseInt(periodKey, 10) < selectedPeriodNumber)
        .forEach(([, teamScores]) => addTeamScores(score, teamScores));

    // The selected period is scored live from goal events up to the playhead.
    playerEvents
        .filter(
            (event) =>
                Number(event.period) === Number(selectedPeriodNumber) &&
                event.frame <= currentFrame &&
                goalDefinitionIds.has(event.defId)
        )
        .forEach((event) => addTeamScores(score, { [event.teamId]: 1 }));

    return score;
};

/**
 * Formats a period clock value (in seconds) into a display string.
 *
 * @param periodTimeSeconds The clock value to format, in seconds
 * @param periodLengthSeconds The actual length of the period, in seconds
 * @param gameClockGoesDown Whether the game clock counts down (default: true)
 */
// eslint-disable-next-line complexity
export const formatOfficialPeriodTime = (
    periodTimeSeconds: number,
    periodLengthSeconds: number,
    gameClockGoesDown = true
): string => {
    const value = Math.max(periodTimeSeconds, 0);

    if (!gameClockGoesDown) {
        const wholeSeconds = Math.floor(value);
        const minutes = Math.floor(wholeSeconds / 60);
        const seconds = wholeSeconds % 60;
        const centisecondsStr = value.toFixed(2).split('.')[1];
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${centisecondsStr}`;
    }

    if (value >= periodLengthSeconds) {
        const totalSeconds = Math.round(periodLengthSeconds);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    const maxMinutes = Math.max(Math.round(periodLengthSeconds / 60) - 1, 0);

    // Truncate (rather than round up) the fractional second so the countdown matches the official
    // period time. The playhead almost always sits between frames, so rounding up would make the
    // displayed clock read one second ahead of the official time.
    const wholeSeconds = Math.floor(value);
    let totalMinutes = Math.floor(wholeSeconds / 60);
    let seconds = wholeSeconds % 60;

    if (totalMinutes > maxMinutes) {
        totalMinutes = maxMinutes;
        seconds = wholeSeconds % 60;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};
